/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { type GeoLocation } from "@src/appV2/Location";
import { useAcceptNegotiation } from "@src/appV2/Negotiations/api/useAcceptNegotiation";
import { useClaimShift } from "@src/appV2/OpenShifts/ShiftAction/api/useClaimShift";
import { type BackgroundCheckMissingDataType } from "@src/appV2/OpenShifts/ShiftAction/types";
import {
  SHIFT_DISCOVERY_BASKET_SIZE_WILL_REACH_WARNING_MODAL_PATH,
  SHIFT_DISCOVERY_ENABLE_LOCATION_PERMISSIONS_PATH,
  SHIFT_DISCOVERY_SHIFT_ARRIVAL_TIME_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_BREAK_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_EDUCATE_ABOUT_CLIPBOARD_SCORE_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_EDUCATE_NEW_WORKER_MODAL_PATH,
} from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftDiscoverySearchMode } from "@src/appV2/redesign/ShiftDiscovery/useShiftDiscoverySearchMode";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { HCF_PROFILE_SOURCE } from "@src/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type History } from "history";
import { useHistory, useLocation } from "react-router-dom";

import { type ComputedArrivalData } from "../../ArrivalTime/computeArrivalData";
import { useIsArrivalTimeEligible } from "../../ArrivalTime/useIsArrivalTimeEligible";
import { isBasketSizeWarningAcknowledgmentDone } from "../../ClipboardScore/hooks/useIsBasketSizeWarningAcknowledgmentNeeded";
import { type NavigateToModalFunction } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { createBackgroundCheckUrlFromMissingDataTypes } from "../createBackgroundCheckUrlFromMissingDataTypes";
import { invalidateShiftModal } from "../Open/useGetShiftModal";
import { useCheckPreBookingState } from "./useCheckPreBookingState";
import { useShouldEducateNewWorker } from "./useShouldEducateNewWorker";
import { useShouldShowClipboardScoreEducation } from "./useShouldShowClipboardScoreEducation";

export const PreBookingCheck = {
  LOCATION_PERMISSION: "location-permission",
  BREAK_POLICY_ACKNOWLEDGEMENT: "break-policy-acknowledgement",
  EDUCATE_NEW_WORKER: "educate-new-worker",
  ARRIVAL_TIME: "arrival-time",
  BOOKING_CONFIRMATION: "booking-confirmation",
  BASKET_SIZE_WILL_REACH_WARNING: "basket-size-will-reach-warning",
  CLIPBOARD_SCORE_EDUCATION: "clipboard-score-education",
  PROVIDE_DATA_FOR_BACKGROUND_CHECK: "provide-data-for-background-check",
} as const;

export type PreBookingCheckType = (typeof PreBookingCheck)[keyof typeof PreBookingCheck];

interface AttemptBookingShiftParams {
  missingBackgroundCheckDataTypes?: BackgroundCheckMissingDataType[];
  geoLocation?: GeoLocation;
  skipChecks?: PreBookingCheckType[];
}

export interface UseBookShiftProps {
  shift: {
    id: string;
    offerId?: string;
    start: string;
    end: string;
    qualification: string;
    computedArrivalData?: ComputedArrivalData;
  };
  workplace: {
    id: string;
    type: string;
    name: string;
    address: {
      formattedAddress: string;
      longitude: string;
      latitude: string;
    };
    timezone: string;
    msa: string;
  };
  workerPastShiftsCount: number | undefined;
  rateNegotiationId: string | undefined;
  navigateToModal: NavigateToModalFunction;
  onBook: () => void;
}

export interface PerformChecksBeforeBookingParams {
  missingBackgroundCheckDataTypes: BackgroundCheckMissingDataType[];
  isBasketSizeWarningAcknowledgmentNeeded: boolean;
  geolocationIsUnavailable: boolean;
  requiresBreakPolicyAcknowledgement: boolean;
  breakPolicyNoteId?: string;
  navigateToModal: NavigateToModalFunction;
  shiftId: string;
  skipChecks: PreBookingCheckType[];
  shouldEducateNewWorker: boolean;
  shouldShowArrivalTimeModal: boolean;
  shouldShowClipboardScoreEducation: boolean;
  history: History;
}

/**
 * Performs pre-booking checks and returns whether booking should proceed
 *
 * Sometimes there is a need to skip certain pre-booking checks.
 * For example, we skip the break policy acknowledgement check when the user has just acknowledged the break policy
 * and we want to proceed with the booking. If we don't skip the check, then the state (that the policy has been acknowledged)
 * is not updated yet when we clients call `attemptBookingShift`. This is because the state is updated asynchronously
 * and `requiresBreakPolicyAcknowledgement` which is in the closure might not be updated yet.
 *
 * @returns true if booking should proceed, false otherwise
 */
export function performChecksBeforeBooking(params: PerformChecksBeforeBookingParams): boolean {
  const {
    missingBackgroundCheckDataTypes,
    geolocationIsUnavailable,
    requiresBreakPolicyAcknowledgement,
    breakPolicyNoteId,
    navigateToModal,
    shiftId,
    skipChecks,
    shouldEducateNewWorker,
    shouldShowArrivalTimeModal,
    isBasketSizeWarningAcknowledgmentNeeded,
    shouldShowClipboardScoreEducation,
    history,
  } = params;

  if (!skipChecks.includes(PreBookingCheck.LOCATION_PERMISSION) && geolocationIsUnavailable) {
    navigateToModal(SHIFT_DISCOVERY_ENABLE_LOCATION_PERMISSIONS_PATH);
    return false;
  }

  if (
    missingBackgroundCheckDataTypes.length > 0 &&
    !skipChecks.includes(PreBookingCheck.PROVIDE_DATA_FOR_BACKGROUND_CHECK)
  ) {
    const url = createBackgroundCheckUrlFromMissingDataTypes({
      queryParams: new URLSearchParams(history.location.search),
      shiftId,
      missingBackgroundCheckDataTypes,
    });
    history.push(url);
    return false;
  }

  if (
    !skipChecks.includes(PreBookingCheck.BASKET_SIZE_WILL_REACH_WARNING) &&
    isBasketSizeWarningAcknowledgmentNeeded &&
    // We call this function here because isBasketSizeWarningAcknowledgmentNeeded is async. updated so can be stale
    // Doing a sync check here to ensure we don't show the modal infinitely
    !isBasketSizeWarningAcknowledgmentDone(shiftId)
  ) {
    navigateToModal(SHIFT_DISCOVERY_BASKET_SIZE_WILL_REACH_WARNING_MODAL_PATH);
    return false;
  }

  if (
    !skipChecks.includes(PreBookingCheck.BREAK_POLICY_ACKNOWLEDGEMENT) &&
    requiresBreakPolicyAcknowledgement &&
    isDefined(breakPolicyNoteId)
  ) {
    navigateToModal(SHIFT_DISCOVERY_SHIFT_BREAK_MODAL_PATH, {
      pathParams: { shiftId, noteId: breakPolicyNoteId },
    });
    return false;
  }

  // This check ensures that the booking confirmation modal is always displayed at the end of the booking process
  // It is a part of the `useBookShift` which is used throughout the app to ensure consistency in the booking flow
  // The only place that would skip this check is the `ConfirmBookingBottomSheet`
  if (!skipChecks.includes(PreBookingCheck.BOOKING_CONFIRMATION)) {
    if (shouldShowArrivalTimeModal) {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_ARRIVAL_TIME_MODAL_PATH, {
        pathParams: { shiftId },
      });
    } else if (shouldEducateNewWorker) {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_EDUCATE_NEW_WORKER_MODAL_PATH, {
        pathParams: { shiftId },
      });
    } else if (shouldShowClipboardScoreEducation) {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_EDUCATE_ABOUT_CLIPBOARD_SCORE_MODAL_PATH, {
        pathParams: { shiftId },
      });
    } else {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH, {
        pathParams: { shiftId },
      });
    }

    return false;
  }

  return true;
}

/**
 * This hook is used to book a shift.
 *
 * When performPreBookingChecks is false (default), it assumes that the device
 * already has geolocation and any other acknowledgements or checks have been completed. It is the
 * caller's responsibility to ensure that the shift is in a bookable state before attempting to
 * book the shift.
 *
 * When performPreBookingChecks is true, it will check for location permissions and break policy
 * acknowledgements before attempting to book the shift.
 *
 * @param props takes in the shift to book, and a callback to call once the booking is successful
 * @returns
 */
export function useBookShift(props: UseBookShiftProps) {
  const { shift, workplace, workerPastShiftsCount, onBook, navigateToModal, rateNegotiationId } =
    props;

  const { id: shiftId, offerId: shiftOfferId, start: shiftStartIso, computedArrivalData } = shift;
  const { id: workplaceId, msa: workplaceMsa } = workplace;

  const {
    userId: workerId,
    email: workerEmail,
    alreadyWorked: workerAlreadyWorked,
  } = useDefinedWorker();
  const searchMode = useShiftDiscoverySearchMode();
  const { showErrorToast } = useToast();

  const { linkSource } = useSearchParams();
  const { pathname: currentPathname } = useLocation();
  const history = useHistory();

  const queryClient = useQueryClient();

  const {
    geolocationIsUnavailable,
    deviceGeolocation,
    requiresBreakPolicyAcknowledgement,
    breakPolicyNoteId,
    isPreBookingChecksLoading,
    isBasketSizeWarningAcknowledgmentNeeded,
  } = useCheckPreBookingState({
    workplaceId,
    shiftId,
  });

  const { mutateAsync: claimShift } = useClaimShift();
  const { mutateAsync: acceptNegotiation } = useAcceptNegotiation();

  const hasComputedArrivalData = isDefined(computedArrivalData);
  const isShortLeadTime = useIsArrivalTimeEligible({
    shiftStartIso,
    workplaceId,
    workplaceMsa: workplaceMsa ?? "",
  });

  const { mutateAsync: attemptClaimShift, isLoading: isBooking } = useMutation(
    async (params: { geoLocation?: GeoLocation }) => {
      const { geoLocation } = params ?? {};
      const { latitude, longitude } = geoLocation ?? {};
      if (!shiftOfferId) {
        showErrorToast(
          "Could not load price for booking this shift. Please re-open the app and try again."
        );
        return;
      }

      const computedArrival = isDefined(computedArrivalData)
        ? {
            durationInMinutes: computedArrivalData.durationInMinutes,
            distanceInMeters: computedArrivalData.distanceInMeters,
            gracePeriodInMins: computedArrivalData.gracePeriodInMinutes,
            targetArrivalTime: computedArrivalData.targetArrivalTime.toISOString(),
            provider: computedArrivalData.provider,
          }
        : undefined;

      const bookedFrom = typeof linkSource === "string" ? linkSource : undefined;
      try {
        await (rateNegotiationId
          ? acceptNegotiation({ negotiationId: rateNegotiationId })
          : claimShift({
              shiftId,
              offerId: shiftOfferId,
              searchMode,
              latitude,
              longitude,
              bookedFrom,
              computedArrival,
            }));

        logEvent(APP_V2_USER_EVENTS.INSTANT_BOOKED_SHIFT, {
          shiftId,
          isShortLeadTime,
          isTargetArrivalTimeDefined: isDefined(computedArrival?.targetArrivalTime),
          computedArrival,

          // Will help determing from which page the user booked the shift
          currentPathname,
        });

        if (linkSource === HCF_PROFILE_SOURCE.PERMANENT_PLACEMENTS_WORK_TRIALS) {
          logEvent(APP_V2_APP_EVENTS.PLACEMENT_WORK_TRIAL_SHIFT_BOOKED, {
            workerId,
            userEmail: workerEmail,
            workerAlreadyWorked,
            workplaceId,
            shiftId,
            source: "instant_book",
          });
        }

        if (rateNegotiationId) {
          await invalidateShiftModal(queryClient, shiftId);
        }

        onBook();
      } catch (error: unknown) {
        const errorMessage =
          (error as { response?: { data?: { displayableMessage?: string } } }).response?.data
            ?.displayableMessage ??
          "Something went wrong while booking this shift. Please try again.";
        showErrorToast(errorMessage);
      }
    }
  );

  const { shouldEducateNewWorker: maybeShouldEducateNewWorker } =
    useShouldEducateNewWorker(workerPastShiftsCount);
  const { shouldShowClipboardScoreEducation: maybeShowClipboardScoreEducation } =
    useShouldShowClipboardScoreEducation(workerPastShiftsCount);

  const attemptBookingShift = async (params?: AttemptBookingShiftParams) => {
    const { geoLocation, skipChecks, missingBackgroundCheckDataTypes } = params ?? {};

    // for educating new worker it should either be educateNewWorker or clipboardScoreEducation
    // with clipboardScoreEducation taking precedence
    const shouldShowClipboardScoreEducation =
      Boolean(maybeShowClipboardScoreEducation) &&
      !skipChecks?.includes(PreBookingCheck.CLIPBOARD_SCORE_EDUCATION);

    const shouldEducateNewWorker =
      maybeShouldEducateNewWorker &&
      !skipChecks?.includes(PreBookingCheck.EDUCATE_NEW_WORKER) &&
      !shouldShowClipboardScoreEducation;

    const shouldProceed = performChecksBeforeBooking({
      missingBackgroundCheckDataTypes: missingBackgroundCheckDataTypes ?? [],
      isBasketSizeWarningAcknowledgmentNeeded,
      skipChecks: skipChecks ?? [],
      geolocationIsUnavailable,
      requiresBreakPolicyAcknowledgement,
      breakPolicyNoteId,
      navigateToModal,
      shiftId,
      shouldEducateNewWorker,
      shouldShowArrivalTimeModal:
        hasComputedArrivalData &&
        isShortLeadTime &&
        isDefined(shiftStartIso) &&
        !skipChecks?.includes(PreBookingCheck.ARRIVAL_TIME),
      shouldShowClipboardScoreEducation,
      history,
    });

    if (!shouldProceed) {
      // If the checks fail, we don't want to attempt to claim the shift
      // The side-effect of the checks are handled in the performChecksBeforeBooking function
      return;
    }

    await attemptClaimShift({
      geoLocation: geoLocation ?? deviceGeolocation?.geoLocation,
    });
  };

  return {
    attemptBookingShift,
    isBooking,
    isLoading: isPreBookingChecksLoading,
  };
}
